import { createContext, use, useState, useEffect } from "react";
import { getFileExtension } from "lib/actions/files";
import { useSupportedFileExtensions } from "lib/hooks/useSupportedFileExtensions";
import { BlobReader, ZipReader } from "@zip.js/zip.js";
import { UploadIssueFileType } from "./lib/types/UploadIssueType";
import { UploadIssue } from "./lib/types/UploadIssue";
import { PreprocessUploadResult } from "./lib/types/PreprocessUploadResult";
import {
  isFbx,
  isUnsupportedFbx,
  isUnsupportedShapefile,
  isShapefile,
  isCompleteFbx,
  isCompleteShapefile,
  is3dTile,
  isComplete3dTile,
  isUnsupported3dTile,
  isSingleFile,
  isFileGeodatabase,
  isCompleteFileGeodatabase,
} from "./lib/zip-file-inspectors";
import { getFilenames, getFilePaths } from "./lib/zip-file-inspectors/utils";
import { v4 as uuid } from "uuid";
import { AssetModel } from "lib/types/assets/AssetModel";
import { UploadIssueReason } from "./lib/types/UploadIssueReason";

type UploadPreprocessorContext = {
  preprocessUpload(file: File): Promise<PreprocessUploadResult>;
  clearUploadIssues(): void;
  goBackToUploadIssuesList(): void;
  uploadIssues: UploadIssue[];
  missingFilesDetected: boolean;
  resolveUploadIssue(
    uploadIssue: UploadIssue,
    file: File,
    claimedAssetType?: AssetModel["type"],
  ): void;
  removeUploadIssue(uploadIssue: UploadIssue): void;
  selectedUploadIssue: UploadIssue;
  setSelectedUploadIssue(uploadIssue: UploadIssue): void;
  selectedUploadIssueType: UploadIssueFileType;
  setSelectedUploadIssueType(type: UploadIssueFileType): void;
};
const UploadPreprocessorContext =
  createContext<UploadPreprocessorContext>(null);

export const UploadPreprocessorProvider = ({ children }) => {
  const supportedFileExtensions = useSupportedFileExtensions();
  const [uploadIssues, setUploadIssues] = useState<UploadIssue[]>([]);
  const [selectedUploadIssue, setSelectedUploadIssue] = useState<UploadIssue>();
  const [selectedUploadIssueType, setSelectedUploadIssueType] =
    useState<UploadIssueFileType>();

  // Missing required files is just one of the possible upload issue kinds/reasons.
  // This particular case receives a revamped UI, so we need to treat it differently.
  const [missingFilesDetected, setMissingFilesDetected] =
    useState<boolean>(false);

  useEffect(() => {
    if (uploadIssues.length > 0) {
      // For missing required files, we only show the first issue of this kind for now (TACO-2482).
      const firstMissingFilesUploadIssue = uploadIssues.find(
        (x) => x.reason === UploadIssueReason.MissingFile,
      );

      setMissingFilesDetected(firstMissingFilesUploadIssue !== undefined);
      if (firstMissingFilesUploadIssue) {
        setSelectedUploadIssue(firstMissingFilesUploadIssue);
      }
    }
  }, [uploadIssues]);

  const preprocessUpload = async (
    file: File,
  ): Promise<PreprocessUploadResult> => {
    const extension = getFileExtension(file.name);

    return new Promise((resolve, reject) => {
      function resolveUnsupported(message: string) {
        resolve({
          isValid: false,
          validationMessage: message,
          isSupported: false,
          preprocessedFile: file,
        });
      }

      switch (extension) {
        case "zip":
          new ZipReader(new BlobReader(file))
            .getEntries()
            .then((entries) => {
              // Ahead of the Shapefile check: a geodatabase holds files with
              // extensions that Shapefile sidecars also use.
              const filePaths = getFilePaths(entries);
              if (isFileGeodatabase(filePaths)) {
                if (!isCompleteFileGeodatabase(filePaths)) {
                  resolveUnsupported(
                    "File Geodatabase zip contains no .gdbtable files.",
                  );
                  return;
                }
                resolve({
                  isValid: true,
                  isSupported: true,
                  preprocessedFile: file,
                });
                return;
              }

              var filenames = getFilenames(filePaths);
              if (isFbx(filenames)) {
                if (isUnsupportedFbx(filenames)) {
                  resolveUnsupported("FBX zip contains unsupported files.");
                } else if (!isCompleteFbx(filenames)) {
                  setUploadIssues((prev) => [
                    ...prev,
                    {
                      id: uuid(),
                      type: UploadIssueFileType.FBX,
                      reason: UploadIssueReason.MissingFile,
                      file,
                      resolve,
                    },
                  ]);
                } else {
                  resolve({
                    isValid: true,
                    isSupported: true,
                    preprocessedFile: file,
                  });
                }
              } else if (isShapefile(filenames)) {
                if (isUnsupportedShapefile(filenames)) {
                  resolveUnsupported(
                    "Shapefile zip contains unsupported files.",
                  );
                } else if (!isCompleteShapefile(filenames)) {
                  setUploadIssues((prev) => [
                    ...prev,
                    {
                      id: uuid(),
                      type: UploadIssueFileType.SHAPEFILE,
                      reason: UploadIssueReason.MissingFile,
                      file,
                      resolve,
                    },
                  ]);
                } else {
                  resolve({
                    isValid: true,
                    isSupported: true,
                    preprocessedFile: file,
                  });
                }
              } else if (is3dTile(filenames)) {
                if (isUnsupported3dTile(filenames)) {
                  resolveUnsupported("3D Tile zip contains unsupported files.");
                } else if (!isComplete3dTile(filenames)) {
                  setUploadIssues((prev) => [
                    ...prev,
                    {
                      id: uuid(),
                      type: UploadIssueFileType.TILESET,
                      reason: UploadIssueReason.MissingFile,
                      file,
                      resolve,
                    },
                  ]);
                } else {
                  console.log("Resolving 3D tile");
                  resolve({
                    isValid: true,
                    isSupported: true,
                    preprocessedFile: file,
                  });
                }
              } else if (isSingleFile(filenames)) {
                console.log("Single file with supported extension");
                resolve({
                  isValid: true,
                  isSupported: true,
                  preprocessedFile: file,
                });
              } else {
                resolveUnsupported("Unsupported zip contents.");
              }
            })
            .catch((e) => {
              console.error(e);
              resolveUnsupported("Unable to unzip file for inspection.");
            });
          break;

        case "tif":
          setUploadIssues((prev) => [
            ...prev,
            {
              id: uuid(),
              type: UploadIssueFileType.TIF,
              file,
              resolve,
            },
          ]);
          break;

        default:
          const isSupported = supportedFileExtensions.has(
            getFileExtension(file.name),
          );
          if (!isSupported) resolveUnsupported("Unsupported file type.");
          else {
            resolve({
              isValid: true,
              isSupported: true,
              preprocessedFile: file,
            });
          }
          break;
      }
    });
  };

  const resolveUploadIssue = (
    uploadIssue: UploadIssue,
    file: File,
    claimedAssetType: AssetModel["type"],
  ) => {
    uploadIssue.resolve({
      isValid: true,
      isSupported: true,
      preprocessedFile: file,
      claimedAssetType,
    });
    setUploadIssues((prev) =>
      prev.filter((issue) => issue.id !== uploadIssue.id),
    );
    setSelectedUploadIssue(null);
  };

  const clearUploadIssues = () => {
    setUploadIssues((prev) => {
      prev.forEach((issue) =>
        issue.resolve({
          isValid: false,
          isSupported: true,
          preprocessedFile: issue.file,
        }),
      );
      return [];
    });
    setSelectedUploadIssue(null);
  };

  const removeUploadIssue = (uploadIssue: UploadIssue) => {
    uploadIssue.resolve({
      isValid: false,
      isSupported: true,
      preprocessedFile: uploadIssue.file,
    });
    setUploadIssues((prev) =>
      prev.filter((issue) => issue.id !== uploadIssue.id),
    );
    setSelectedUploadIssue(null);
  };

  const goBackToUploadIssuesList = () => {
    setSelectedUploadIssue(null);
  };

  return (
    <UploadPreprocessorContext
      value={{
        preprocessUpload,
        uploadIssues,
        missingFilesDetected,
        goBackToUploadIssuesList,
        resolveUploadIssue,
        removeUploadIssue,
        selectedUploadIssue,
        setSelectedUploadIssue,
        clearUploadIssues,
        selectedUploadIssueType,
        setSelectedUploadIssueType,
      }}
    >
      {children}
    </UploadPreprocessorContext>
  );
};

export const useUploadPreprocessor = () => use(UploadPreprocessorContext);
